import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { signOut } from '../../utils/firebase';
import { useSubscriptions } from '../../redux/slices/subscriptions/useSubscriptions';
interface MenuItem {
  name: string;
  path: string;
  features: string[];
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuItems: MenuSection[] = [
  {
    title: 'Services',
    items: [
      { name: 'Leads', path: '/expert/collect', features: ['tl_unlimited_leads'] },
      { name: 'Meetings', path: '/expert/meet', features: ['tl_byo_meeting'] },

    ],
  },
  {
    title: 'Settings',
    items: [
      { name: 'My Profile', path: '/expert/profile', features: ['tl_unlimited_leads'] },
      { name: 'Business Details', path: '/expert/business', features: ['tl_business'] },
      { name: 'Service Details', path: '/expert/service-details', features: ['tl_unlimited_leads'] },
      { name: 'Meeting Settings', path: '/expert/meet/settings', features: ['tl_byo_meeting'] },
      { name: 'Billing', path: '/expert/billing', features: [] },
      { name: 'Team Management', path: '/expert/team', features: ['tl_business_teams'] },
    ],
  },
  {
    title: '',
    items: [

      { name: 'Support', path: '/expert/support', features: [] },
    ],
  }
];

const ExpertSidebar: React.FC = () => {
  const { entitledFeatures } = useSubscriptions();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isMobile = window.innerWidth <= 768;

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login'); // Redirect to login page
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const isActive = (path: string) => {
    return window.location.pathname === path;
  };

  const isEnabled = (features: string[]) => {
    const entitledFeaturesSet = new Set(entitledFeatures)
    return features.every(feature => entitledFeaturesSet.has(feature))
  }

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <MobileHeader>
        <IconButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <CloseIcon sx={{ color: 'white' }} /> : <MenuIcon sx={{ color: 'white' }} />}
        </IconButton>
        <MobileLogo>Triplink.VIP</MobileLogo>
      </MobileHeader>

      <SidebarContainer
        $isMobile={isMobile}
        $isOpen={isMobileMenuOpen}
        onClick={(e) => {
          if (isMobile && e.target === e.currentTarget) {
            setIsMobileMenuOpen(false);
          }
        }}
      >
        <SidebarContent $isMobile={isMobile}>
          {!isMobile && <SidebarLogo>Triplink.VIP</SidebarLogo>}

          <MainMenu>
            {menuItems.map((section, index) => (
              <SidebarSection key={index}>
                <SidebarHeader>{section.title}</SidebarHeader>
                {section.items.map((item, itemIndex) => (
                  <SidebarItem
                    key={itemIndex}
                    active={isActive(item.path)}
                    enabled={isEnabled(item.features)}
                    onClick={() => isEnabled(item.features) ? handleNavigation(item.path) : null}
                  >
                    {item.name}
                  </SidebarItem>
                ))}
              </SidebarSection>
            ))}
          </MainMenu>

          <BottomMenu>
            <SidebarItem onClick={handleLogout} enabled>
              Logout
            </SidebarItem>
          </BottomMenu>
        </SidebarContent>
      </SidebarContainer>
    </>
  );
};

const MobileHeader = styled.div`
  display: none;
  z-index: 1100;
  background-color: #1E242A;
  color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 64px;
  position: fixed;
  width: 100%;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 64px 1fr;
    align-items: center;
    justify-items: center;
  }
`;

const MobileLogo = styled.div`
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  text-align: center;
`;

const SidebarContainer = styled.div<{ $isMobile: boolean; $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1200;
  width: 240px;
  overflow-y: auto;
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  background-color: #1E242A;

  @media (max-width: 768px) {
    transform: translateX(${props => (props.$isOpen ? '0' : '-100%')});
    top: 64px;
    height: calc(100vh - 64px);
  }
`;

const SidebarContent = styled.div<{ $isMobile: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${props => props.$isMobile ? '0.5rem 0' : '1rem 0'};
  color: white;
`;

const MainMenu = styled.div`
  flex: 1;
  overflow-y: auto;
  
  /* Hide scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BottomMenu = styled.div`
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SidebarLogo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarSection = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 1.25rem;
    right: 1.25rem;
    height: 2px;
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const SidebarHeader = styled.div`
  color: #F6AD55;
  font-size: 0.75rem;
  padding: 0.375rem 1.25rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SidebarItem = styled.div<{ active?: boolean, enabled: boolean }>`
  cursor: pointer;
  padding: 0.5rem 1.25rem;
  background-color: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.enabled ? 'white': 'grey'};
  font-style: ${props => props.enabled ? 'normal' : 'italic'};
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export default ExpertSidebar;
